/**
 * Life4Earth V2 — React hook for one Mini-Project.
 *
 * Loads a single Mini-Project by guid. Returns null on 404 so callers can
 * cleanly handle "not a Mini-Project" without try/catch (the dispatcher
 * uses this to decide whether to render the Mini page or fall through to
 * the Full Project page).
 *
 * Stage 3a — minimal version, no queryCache integration yet.
 * Cache wiring + sub-entity hooks land in stage 3b.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchMiniProject,
  EVT_MINI_PROJECT_MUTATED,
  type MiniProject,
} from "./miniProjects";

export interface UseApiMiniProjectResult {
  miniProject: MiniProject | null;
  loading: boolean;
  error: string | null;
  /** True iff the GET returned 404 (not a Mini-Project). */
  notFound: boolean;
  refetch: () => void;
}

export function useApiMiniProject(
  miniProjectGuid: string | null | undefined
): UseApiMiniProjectResult {
  const mountedRef = useRef(true);
  const [state, setState] = useState<UseApiMiniProjectResult>({
    miniProject: null,
    loading: true,
    error: null,
    notFound: false,
    refetch: () => {},
  });

  const refetch = useCallback(async () => {
    if (!miniProjectGuid) {
      setState({
        miniProject: null,
        loading: false,
        error: null,
        notFound: false,
        refetch,
      });
      return;
    }

    // Only show the FULL-PAGE loading state on the initial load. A background
    // refetch (fired by EVT_MINI_PROJECT_MUTATED after a task/goal/BOM/status
    // write) must NOT flip loading=true — the detail page does
    // `if (loading) return <Loading…>`, which would unmount the whole tree
    // mid-edit, collapsing open trays and snapping the active sub-tab back to
    // Summary. When we already have data, keep rendering it and just swap in
    // the fresh result on success. (DEV-1289 follow-up — root cause of the
    // "marking a task done closes the tray / jumps to Summary" bug.)
    setState((prev) =>
      prev.miniProject
        ? { ...prev, error: null }
        : { ...prev, loading: true, error: null },
    );

    try {
      const result = await fetchMiniProject(miniProjectGuid);
      if (!mountedRef.current) return;
      setState({
        miniProject: result,
        loading: false,
        error: null,
        notFound: result === null,
        refetch,
      });
    } catch (err) {
      if (!mountedRef.current) return;
      setState({
        miniProject: null,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
        notFound: false,
        refetch,
      });
    }
  }, [miniProjectGuid]);

  useEffect(() => {
    mountedRef.current = true;
    refetch();
    return () => {
      mountedRef.current = false;
    };
  }, [refetch]);

  // Skip 2026-05-28 — refetch on MP-scoped mutation events so the MP
  // record's last_data_update_at (and other fields touched by sub-entity
  // writes) propagates back to the detail page. Without this, the Data
  // Reliability KPI dot and the DR sub-tab stay stale after a status
  // save, even when the backend correctly bumps the timestamp.
  // EVT_MINI_PROJECT_MUTATED is fired by every status/goal/task/BOM
  // mutation that calls dispatchMiniProjectMutated. We filter on guid
  // so a mutation on a different MP doesn't trigger this hook's
  // network call.
  useEffect(() => {
    if (!miniProjectGuid) return undefined;
    const targetGuid = miniProjectGuid.toUpperCase();
    const onMutated = (e: Event) => {
      const detail = (e as CustomEvent).detail as { miniProjectGuid?: string } | undefined;
      if (!detail?.miniProjectGuid) return;
      if (detail.miniProjectGuid.toUpperCase() !== targetGuid) return;
      refetch();
    };
    if (typeof window === "undefined") return undefined;
    window.addEventListener(EVT_MINI_PROJECT_MUTATED, onMutated);
    return () => window.removeEventListener(EVT_MINI_PROJECT_MUTATED, onMutated);
  }, [miniProjectGuid, refetch]);

  return { ...state, refetch };
}
